import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ConfigService,
  DataService,
  IndexedDBPropertiesService,
  LocalStorageKeys,
  LoggedInUserService,
  OfflineStatusService,
  ClientResourceService,
  SessionTestService,
  select,
  OfflineUtilService,
  IndexedDBService,
  DexieWorkerService
  
} from '@formbird/services';
import { User, OfflineStatus } from '@formbird/types';
import {
  SharedUrlRoutes,
  UtilArray,
  UtilError
} from '@formbird/shared';
import { Observable } from 'rxjs';
import { CacheMapTilesService } from './tileserver-gl/cache-map-tiles.service';
import { RouterService } from '../../app-routing/router.service';

const serverRoutes = SharedUrlRoutes.serverRoutes;
const strLastSyncStaticResourceCount = "lastSyncStaticResourceCount";
const strLastSyncComponentCount = "lastSyncComponentCount";
const logger = console;

@Injectable({
  providedIn: 'root'
})
export class CacheLoadService {

  @select(['userState', 'user']) user$: Observable<User>;
  @select(['offlineReducer', 'offlineStatus']) offlineStatus$: Observable<OfflineStatus>;

  user: User;
  connected: boolean;

  vendorCacheName: string = "formbird-vendor-libs";

  constructor(
    private dataService: DataService,
    private offlineStatusService: OfflineStatusService,
    private loggedInUserService: LoggedInUserService,
    private indexedDBPropertiesService: IndexedDBPropertiesService,
    private configService: ConfigService,
    private routingService: RouterService,
    private http: HttpClient,
    private clientResourceService: ClientResourceService,
    private cacheMapTilesService: CacheMapTilesService,
    private sessionTestService: SessionTestService,
    private offlineUtilService: OfflineUtilService,
    private indexedDBService: IndexedDBService,
    private dexieWorkerService: DexieWorkerService,
  ) {
    this.user$.subscribe((user: User) => this.user = user);

    this.connected = true;
    this.offlineStatusService.offlineStatus$.subscribe((offlineStatusData: any) => {
      this.connected = offlineStatusData.connected;
    });

    this.offlineStatus$.subscribe((offlineStatusData) => {
      this.connected = offlineStatusData.connected;
    });

  }

  private checkStorageQuota() {
    const minimumQuotaMegabytes = this.configService.clientConfig().minimumQuotaMegabytes || 2048;
    return new Promise(resolve => {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
            navigator.storage.estimate().then(estimate => {
                // Convert the quota from bytes to megabytes
                const quotaInMegabytes = estimate.quota / 1024 / 1024;
                resolve(quotaInMegabytes >= minimumQuotaMegabytes);
            });
        } else {
            // Resolve as true if storage estimation is not supported
            logger.warn("Storage quota cannot be estimated in this browser. " +
              "Please consider using a browser that supports navigator.storage.estimate");
            resolve(true);
        }
    })
  }

  /**
   * enable caching
   */
  async enableCaching() {
    const isQuotaSufficient = await this.checkStorageQuota();
    if (!isQuotaSufficient) {
      throw new Error('There is not enough space to enable offline.');
    }

    if (this.offlineStatusService.isCachingEnabled()) {
      return Promise.resolve('Caching is already enabled');
    }

    // https://developers.google.com/web/tools/workbox/
    // guides/storage-quota#special_chrome_incognito_considerations
    const requiredBytes = 124000000;

    const browserQuota = await this.getStorageQuota();

    // allow offline if cant detect browser quota
    if (browserQuota !== 0 && browserQuota < requiredBytes) {
      throw new Error('Offline mode is disabled in private window.');
    }

    this.offlineStatusService.setCachingEnabledStatus(true);

    // At this point, caching hasn't been enabled yet.
    this.offlineStatusService.setCacheLoadingStatus(true); //isCacheLoading = true
    this.offlineStatusService.setCachingAppStatus(true);
    this.offlineStatusService.setInitialCachingComplete(false);
    this.loggedInUserService.setUserConfigItem('lastSyncTileServerCacheCount', 0);
    this.loggedInUserService.setUserConfigItem(strLastSyncStaticResourceCount, 0)
    this.loggedInUserService.setUserConfigItem(strLastSyncComponentCount, 0)

    let cacheTemplate = null;
    const cachingEnableTemplate = this.configService.clientConfig().cachingEnableTemplate;
    if (cachingEnableTemplate) {
      if (cachingEnableTemplate.length) {
        cacheTemplate = cachingEnableTemplate[0].documentId;
      }
    }
 
    try {
      await this.sessionTestService.testSession();
      await this.loadIndexedDBIndexes();
      await this.dataService.initialise();

      if (cacheTemplate) {
        this.asyncChangeState(cacheTemplate);
      }

      await this.storeOfflineUserCredentials();
      await this.cacheAddons();
      await this.doCaching();

      this.dexieWorkerService.initWorker();
      this.offlineUtilService.startOfflinePoller();

      await this.succLoadingCache();

    } catch (err) {
      return await this.errLoadingCache(err);
    }
  }

  private async doCaching() {

    await Promise.all([this.cacheStaticAssets(), this.loadDataCache(), this.cacheMapTilesService.loadDataCache()]);
  }

  private async cacheAddons() {
    const dexieAddons = this.configService.clientConfig().offline?.dexieAddons || [];
    const cacheName = "formbird-dexie-addons";
    const cacheStorage = await caches.open(cacheName);

    for (const dexieAddon of dexieAddons) {
      cacheStorage.add(dexieAddon.fileName);
    }
  }

  private loadIndexedDBIndexes() {

    const _self = this;

    return _self.dataService.getIndexedDBIndexes().then(
      function(templateIndexes) {
        templateIndexes = _self.indexedDBService.getValidIndexNames(templateIndexes);

        let indexes = _self.loggedInUserService.getUserConfigItem('searchIndexes');
        indexes = UtilArray.union(indexes, templateIndexes);
        _self.loggedInUserService.setUserConfigItem('searchIndexes', indexes);

        return Promise.resolve();
      },
      function(err) {
        logger.warn('Could not get indexedDBIndexes. If offline, new IndexedDB indexes cannot be created');
        return Promise.reject(err);
      });
  }

  private async getStorageQuota() {
    const nav: any = navigator;
    if (nav?.storage?.estimate) {
        const estimate = await nav.storage.estimate();
        return estimate.quota;
    }

    return 0; // cant read storage quota
  }

  private asyncChangeState(cacheTemplate) {
    this.routingService.navigate(['form', cacheTemplate]);
  }

  private succLoadingCache() {
    
    console.debug('Caching loaded successfully.');

    this.offlineStatusService.setCacheLoadingStatus(false);
    this.offlineStatusService.offlineStatus.currentCacheAttachedFileCount = 0;
    this.offlineStatusService.setInitialCachingComplete(true);
    this.offlineStatusService.offlineStatus.currentCacheCount = 0;
    this.loggedInUserService.setUserConfigItem('currentCacheCount', 0);
    const errorCacheCount = this.offlineStatusService.getErrorCacheCount();
    if (!errorCacheCount) {
      return Promise.resolve();
    } else {
      const msg = 'There are ' + errorCacheCount + ' offline documents that were not cached successfully.';
      logger.error(msg);
      return Promise.reject(new Error(msg));
    }

  }

  private async errLoadingCache(err) {

    console.debug('Caching loaded failed.');

    await this.dataService.clearData();
    this.offlineStatusService.setCacheLoadingStatus(false);
    this.offlineStatusService.setCachingEnabledStatus(false);
    this.offlineStatusService.setInitialCachingComplete(false);
    logger.error('Error on enabling cache. See trace below:');
    logger.error(err);
    if (err.name === UtilError.SERVER_UNRESPONSE_ERROR) {
      return Promise.reject(UtilError.createError('User is offline. Can not enable caching.'));
    }

    return Promise.reject(err);
  }

  private storeOfflineUserCredentials() { //see restoreOfflineUserCredentials

    const _self = this;

    const loggedUserKey = LocalStorageKeys.LOGGED_IN_USER;
    const loggedUser = _self.user.account;

    return _self.indexedDBPropertiesService.setItem(loggedUserKey, loggedUser).then(function() {

      const controlDocumentKey = LocalStorageKeys.LOGGED_IN_ACCOUNT_CONTROL_DOCUMENT;
      const controlDocument = _self.user.accountControlDocument;

      return _self.indexedDBPropertiesService.setItem(controlDocumentKey, controlDocument);

    });
  }

  restoreOfflineUserCredentials() {

    const _self = this;

    const loggedAccount = _self.user.account;
    if (loggedAccount != null) { // no need for restoration
      return Promise.resolve();
    }

    const loggedUserKey = LocalStorageKeys.LOGGED_IN_USER;

    return _self.indexedDBPropertiesService.getItem(loggedUserKey).then(function(result: any) {

      if (result && result.value) {
        _self.loggedInUserService.setUser(result.value);
      } else {
        throw new Error('Failed to retrieve logged user info from IndexedDBPropertiesService');
      }

      const controlDocumentKey = LocalStorageKeys.LOGGED_IN_ACCOUNT_CONTROL_DOCUMENT;

      return _self.indexedDBPropertiesService.getItem(controlDocumentKey);

    }).then(function(result: any) {

      if (result && result.value) {
        _self.loggedInUserService.setUserControlDocument(result.value);
      } else {
        throw new Error('Failed to retrieve logged user control documents from IndexedDBPropertiesService');
      }

    });

  }

  private setStaticResourceStatus() {
    const _self = this;
    if (_self.offlineStatusService.offlineStatus.currentStaticResourceCount
      >= _self.offlineStatusService.offlineStatus.totalStaticResourceCount
      && _self.offlineStatusService.offlineStatus.totalStaticResourceCount !== 0) {
      _self.offlineStatusService.setCachingAppStatus(false);
    } else{
      _self.offlineStatusService.setOfflineStatusToLocalStorage();
    }

  }

  /**
   * cache the static assets that make up the web app, such as html, css, images - so that the static app can be accessed
   * offline. Making a request to each asset is enough to cache it for offline use. The core static assets are cached as part
   * of the workbox service worker built when a new release is built, but components and the libraries that they load are not
   * part of the build because they can't be determined at build time
   */
  async cacheStaticAssets() {

    return new Promise(async (resolve) => {

      let hasClientResourcesLoaded = false;
      let clientResources: any;
      let interval;

      const loadClientResources = async () => {
        clientResources = await this.clientResourceService.getClientResources();
        hasClientResourcesLoaded = true;

        this.offlineStatusService.setTotalStaticResourceCount(clientResources.componentResources?.length + clientResources.resources?.length);

        const lastSyncResourceCount = this.loggedInUserService.getUserConfigItem(strLastSyncStaticResourceCount) || 0;
        const lastSyncComponentCount = this.loggedInUserService.getUserConfigItem(strLastSyncComponentCount) || 0;
        this.offlineStatusService.offlineStatus.currentStaticResourceCount = lastSyncResourceCount + lastSyncComponentCount;

        await this.processStaticAssets(clientResources, lastSyncComponentCount, true);
        await this.processStaticAssets(clientResources, lastSyncResourceCount, false);

        this.offlineStatusService.offlineStatus.currentStaticResourceCount = 0;
        this.offlineStatusService.setAssetsCachingCompleted(true);
        this.offlineStatusService.checkCachingCompleted();

        console.log('Done caching static assets.');
        
        resolve(true);

      };

      try {

        this.offlineStatusService.setAssetsCachingCompleted(false);

        await loadClientResources();

      } catch (e) {

        interval = setInterval(async () => {
          if (!hasClientResourcesLoaded && this.offlineStatusService.offlineStatus.cacheLoading) {
            try {
              await loadClientResources();
              clearInterval(interval);
            } catch (err) {
              console.log('Error caching static assets. Retry... ');
            }
          }
        }, 5000);

      }

    });
    
  }

  private async addCacheStorage(url, cacheStorage) {
    try {

      if (url) {

        if (cacheStorage) {
          url = (url.startsWith('http') || url.startsWith('/')) ? url : '/' + url;

          const vendorLibraryBasePath = this.configService.clientConfig().vendorLibraryBasePath || '';
          url = url.replace('${vendorLibraryBasePath}', vendorLibraryBasePath);
          const res = await cacheStorage.match(url);
          if (!res) {
            await cacheStorage.add(url);
          }

        } else {

          const assetReq = new Request(url, { credentials: 'include' });

          await fetch(assetReq);
        }

      }

    } catch (e) {
      console.warn('Add cache failed for url: ', url);
      return e;
    }
  }

  private async processStaticAssets(clientResources, lastSyncCount, isComponent) {

    const listUrls = [];
    let strLastSyncCount;
    let loopCount = 0;

    function forEachStaticAsset(url) {
      listUrls.push(url);
    }

    if (isComponent) {
      strLastSyncCount = strLastSyncComponentCount;
      clientResources.componentResources.map(forEachStaticAsset);
    } else {
      strLastSyncCount = strLastSyncStaticResourceCount;
      clientResources.resources.map(forEachStaticAsset);
    }

    const confOffline = this.configService.clientConfig().offline;
    let retryInterval = confOffline && confOffline.cacheRetryInterval > 0 ? confOffline.cacheRetryInterval : 10000; //default to 10seconds

    const cacheStorage = !isComponent ? await caches.open(this.vendorCacheName) : null;

    const job = async (url) => {

      const _self = this;
      
      return new Promise(async (resolve, reject) => {
      
        if (_self.connected) {
      
          if (!this.offlineStatusService.offlineStatus.cacheLoading) {
            const errMsg = "Caching static assets terminated due to cache loading cancelled";
            throw new Error(errMsg);
          }

          const err = await this.addCacheStorage(url, cacheStorage);

          if (!err && (!_self.connected)) {
        
            reject(err);

          } else {

            this.offlineStatusService.offlineStatus.currentStaticResourceCount++;
            lastSyncCount++;
            this.loggedInUserService.setUserConfigItem(strLastSyncCount, lastSyncCount);
            this.setStaticResourceStatus();

            resolve(true);
        
          }
        
        } else {

          loopCount++;

          const interval = loopCount < 60 ? 1000 : retryInterval;
          setTimeout(function () {
            reject(new Error("Error on Caching static assets.  Device is not connected"));
          }, interval);
        }

      });
    }

    if (lastSyncCount > 0) {
      listUrls.splice(0, lastSyncCount);
    }

    return this.processList(0, listUrls, job);
  }

  private async processList(startIndex, listUrls, asyncJob) {

    return new Promise(async (resolve, reject) => {
      const promises = [];

      for (let i = startIndex; i < listUrls.length; i++) {
        if (this.loggedInUserService.getUserConfigItem('isRunningCacheClear')  
          || !this.offlineStatusService.isCachingEnabled()) {
          break;
        }
        
        const url = listUrls[i];
        try {
          await asyncJob(url);
        } catch (err) {
          i = i - 1;
        }
        promises.push(i);
      }

      Promise.all(promises).then(
        function successFunction(results) {
          resolve(results);
        },
        function errorFunction(err) {
          reject(new Error(err.message));
        }
      );
    });
  }

  async loadDataCache(){
    const self = this;
    let loopCount = 0;
    let lastErr;
    const confOffline = this.configService.clientConfig().offline;
    let retryInterval = confOffline && confOffline.cacheRetryInterval > 0 ? confOffline.cacheRetryInterval : 10000; //default to 10seconds

    function processDataCache(resolve, reject) {
      
      if (self.connected) {

        self.offlineStatusService.offlineStatus.errorCacheAttachedFile = [];
        self.offlineStatusService.offlineStatus.currentCacheCount = 0;
        self.offlineStatusService.offlineStatus.maxCacheCount = 0;
        self.offlineStatusService.offlineStatus.currentCacheAttachedFileCount = 0;
        self.offlineStatusService.offlineStatus.maxCacheAttachedFileCount = 0;
        self.loggedInUserService.setUserConfigItem('currentCacheCount', 0);
        self.loggedInUserService.setUserConfigItem('cachingEnableDate', new Date().valueOf());
       
        self.dataService.workerLoadDataCache().then(function success(results) {

          resolve(results);

        }, function error(err) {

          lastErr = err;
          logger.error("Error on load data cache : " + err.message);

          if (!self.connected) {
            
            processDataCache(resolve, reject);

          } else {
            reject(err);
          }

        });

      } else {

        loopCount++;

        // We could retry less frequently after 60 seconds, based on cacheRetryInterval config.
        // We want it to keep trying until the connection is regained
        const interval = loopCount < 60 ? 1000 : retryInterval;
        setTimeout(function () {
          if (lastErr) {
            logger.error("Error on load data cache : " + lastErr.message);
          }
          console.log('Just wait for an interval to restart data cache worker...');
          processDataCache(resolve, reject);
        }, interval);

      }

    }


    return new Promise((resolve, reject) => {

      console.debug('Start loading data cache. Offline status: ', self.offlineStatusService.offlineStatus);

      processDataCache(resolve, reject);

    });


  }

}
